// @ts-strict-ignore
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
    AppleAppStorePhasedReleaseState,
    AppPlatform,
    AppStoreType,
    AutomationAutoStagedRollout,
    AutomationType,
    CompletedReleaseSummary,
    GooglePlayStoreState,
    Integration,
    PhasedRelease,
    SubscriptionFeatureType,
} from '../../types';
import { AppComponentProps, AppRouteComponentMatchParams } from '../../typescriptHelpers';
import { ReleasePilotWidget } from '@/components/appHome/widgets/ReleasePilotWidget';
import { TargetDatesWidget } from '@/components/appHome/widgets/TargetDatesWidget';
import { ReleaseStepsWidget } from '@/components/appHome/widgets/ReleaseStepsWidget';
import { AppHomeCard } from '@/components/appHome/AppHomeCard';
import Tag from 'antd/lib/tag';
import { antGrey, runwayColors, titleStyle } from '@/components/styles/globalStyles';
import { Divider, Space, Typography } from 'antd/lib';
import { BuildSelectorComponent } from '@/components/sharedComponents/buildSelector/BuildSelectorComponent';
import {
    getIsAppleAppStore,
    stabilityIssuesSupportedForIntegration,
    tryDeterminePhasedReleaseDaysToPercentageSchedule,
} from '@/helpers';
import StabilityIssuesContainer from '@/components/monitoring/StabilityIssuesContainer';
import { LiveReleaseChartWidget } from '@/components/appHome/widgets/LiveReleaseChartWidget';
import { MonitoringMetricsWidget } from '@/components/appHome/widgets/MonitoringMetricsWidget';
import { ReleaseSummaryWidget } from '@/components/appHome/widgets/ReleaseSummaryWidget';
import { RootState } from '@/reducers/reducers';
import {
    selectAppSettingAutomations,
    selectAutomationById,
    selectIsWorkflowsCIStepEnabled,
    selectReleaseStepsBlockingConfigForReleaseType,
    selectShouldHideNonBlockingSteps,
} from '@/selectors/selectors';
import { useSelector } from 'react-redux';
import { FeatureCompletionWidget } from './widgets/FeatureCompletionWidget';
import { ReleaseMetricsWidget } from '@/components/appHome/widgets/ReleaseMetricsWidget';
import { CollapsibleSection } from '../monitoring/CollapsibleSection';
import RatingsReviews from '../RatingsReviews';
import { PlanRestrictedFeatureWrapper } from '@/components/planRestrictions/PlanRestrictedFeatureWrapper';

interface LiveReleaseSummaryProps extends AppComponentProps {
    summary: CompletedReleaseSummary;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    stabilityIntegration: Integration;
    appStoreIntegration: Integration;
}

export const LiveReleaseComponent: React.FC<LiveReleaseSummaryProps> = (props) => {
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const shouldHideNonBlockingSteps = useSelector((state: RootState) =>
        selectShouldHideNonBlockingSteps(state, appIdParam),
    );
    const isWorkflowsCIStepEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsCIStepEnabled(state, appIdParam),
    );
    const nonBlockingStepsConfigMap = useSelector((state: RootState) =>
        selectReleaseStepsBlockingConfigForReleaseType(state, appIdParam),
    );

    const [tagDisplayText, tagDisplayColor] = tagDisplayString(props.summary.phasedRelease);

    const appStoreSelectedBuilds = props.summary.appStoreBuilds;
    const [selectedBuild, setSelectedBuild] = React.useState(
        props.summary.appStoreBuilds?.length > 0 ? props.summary.appStoreBuilds[0] : null,
    );

    const handleBuildSelect = (value) => {
        const matchedBuild = appStoreSelectedBuilds.find((build) => build.appStoreBuild.id === value);
        setSelectedBuild(matchedBuild);
    };

    const automations = useSelector((state: RootState) => selectAppSettingAutomations(state, appIdParam));

    const autoStagedRolloutAutomationId = automations.find(
        (automation) => automation.type === AutomationType.AutoStagedRollout,
    )?.id;

    const showStabilityIssues =
        selectedBuild &&
        selectedBuild.stabilityInfo &&
        stabilityIssuesSupportedForIntegration(selectedBuild.stabilityInfo.integrationId);

    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, props.appId, autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const automatedStagedRolloutEnabled = autoStagedRolloutAutomation?.enabled;

    const hasPhasedReleaseState = !!props.summary.phasedRelease;

    const isAppleAppStore = getIsAppleAppStore(props.appStoreType);

    // for iOS, just whether a phased release is present. For android, whether they've enabled automated rollouts
    const isAutomatedPhasedRollout = isAppleAppStore ? hasPhasedReleaseState : automatedStagedRolloutEnabled;

    // Stopgap, to deprecate after WFs migration of rollouts step
    // Replace with direct value of daysToPercentSchedule field on DestinationPhasedRelease once possible
    const daysToPercent = tryDeterminePhasedReleaseDaysToPercentageSchedule(
        hasPhasedReleaseState,
        props.appStoreType,
        autoStagedRolloutAutomation,
    );

    return (
        <AppHomeCard
            appId={appIdParam}
            orgId={orgIdParam}
            releaseId={props.summary.releaseId}
            id={props.summary.id}
            displayableVersion={props.summary.displayableVersion}
            releaseName={props.summary.releaseName}
            borderColor={antGrey.grey5}
            backgroundColor={antGrey.grey2}
            headerComponent={
                <>
                    <Space
                        align="center"
                        split={<Divider type="vertical" style={{ minHeight: 26, backgroundColor: antGrey.grey5 }} />}
                        wrap
                    >
                        {tagDisplayText && (
                            <Tag
                                style={{
                                    ...titleStyle,
                                    fontSize: 11,
                                    lineHeight: '22px',
                                    color: runwayColors.white,
                                }}
                                color={tagDisplayColor}
                            >
                                {tagDisplayText}
                            </Tag>
                        )}

                        {selectedBuild && (
                            <BuildSelectorComponent
                                appStoreSelectedBuilds={appStoreSelectedBuilds}
                                selectedBuild={selectedBuild}
                                handleBuildSelect={handleBuildSelect}
                            />
                        )}
                    </Space>
                </>
            }
            contentComponent={
                <>
                    <ReleaseSummaryWidget summaryMessage={props.summary.releaseSummaryMessage} />
                    <ReleaseMetricsWidget
                        orgId={orgIdParam}
                        appId={appIdParam}
                        releaseId={props.summary.releaseId}
                        stats={props.summary.stats}
                    />
                    <LiveReleaseChartWidget
                        isAutomatedPhasedRollout={isAutomatedPhasedRollout}
                        daysToPercent={daysToPercent}
                        appStoreType={props.appStoreType}
                        selectedBuild={selectedBuild}
                        appStoreIntegrationId={props.appStoreIntegration?.id}
                        stabIntegrationId={props.stabilityIntegration?.id}
                        phasedRelease={props.summary.phasedRelease}
                    />
                    <MonitoringMetricsWidget orgId={orgIdParam} appId={appIdParam} summary={props.summary} />
                    <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts}>
                        {(isFeatureDisabled) => {
                            if (isFeatureDisabled) {
                                return (
                                    <div
                                        style={{
                                            backgroundColor: runwayColors.white,
                                            padding: 16,
                                            borderRadius: 8,
                                            border: `solid 1px ${antGrey.grey5}`,
                                            width: '100%',
                                        }}
                                    >
                                        <Typography.Text style={{ color: antGrey.grey7, fontSize: 12 }}>
                                            Stability issues are not available on your current plan.
                                        </Typography.Text>
                                    </div>
                                );
                            }

                            if (showStabilityIssues) {
                                return <StabilityIssuesContainer appId={props.appId} build={selectedBuild} />;
                            }

                            return null;
                        }}
                    </PlanRestrictedFeatureWrapper>
                    {props.summary.appStoreReviewData && (
                        <div style={{ marginTop: 16 }}>
                            <CollapsibleSection title="Reviews" integrationType={props.appStoreIntegration?.id}>
                                <RatingsReviews
                                    reviewData={props.summary.appStoreReviewData}
                                    orgId={props.orgId}
                                    appId={props.appId}
                                />
                            </CollapsibleSection>
                        </div>
                    )}
                </>
            }
            sideComponent={
                <>
                    <TargetDatesWidget appId={appIdParam} summary={props.summary} />
                    <FeatureCompletionWidget featuresStep={props.summary.featuresStep} />
                    <Divider
                        style={{
                            margin: '16px 0',
                        }}
                    />
                    <ReleaseStepsWidget
                        steps={props.summary.steps}
                        platform={props.platform}
                        phasedRelease={props.summary.phasedRelease}
                        appStoreType={props.appStoreType}
                        shouldHideNonBlockingSteps={shouldHideNonBlockingSteps}
                        nonBlockingStepsConfigMap={nonBlockingStepsConfigMap}
                        releaseType={props.summary.type}
                        isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                    />
                    <Divider
                        style={{
                            margin: '16px 0',
                        }}
                    />
                    <ReleasePilotWidget
                        appId={appIdParam}
                        releasePilotId={props.summary.releasePilotId}
                        onReleaseOverview={false}
                    />
                </>
            }
        />
    );
};

const tagDisplayString: (phasedReleaseData?: PhasedRelease) => [string, string] = (phasedReleaseData) => {
    if (phasedReleaseData) {
        const isAutoHalted = phasedReleaseData.isRunwayPaused;

        switch (phasedReleaseData.state) {
            case GooglePlayStoreState.Halted:
                return [isAutoHalted ? 'Halted by Runway' : 'Halted', runwayColors.statusRed];
            case AppleAppStorePhasedReleaseState.Paused:
                return [isAutoHalted ? 'Paused by Runway' : 'Paused', runwayColors.statusRed];
            case GooglePlayStoreState.InProgress:
                return ['Staged rollout in progress', runwayColors.statusGreen];
            case AppleAppStorePhasedReleaseState.Active:
                return ['Phased release in progress', runwayColors.statusGreen];
            case AppleAppStorePhasedReleaseState.Complete:
            case GooglePlayStoreState.Completed:
                return ['Live', runwayColors.statusGreen];
            case GooglePlayStoreState.Draft:
            case GooglePlayStoreState.StatusUnspecified:
            case AppleAppStorePhasedReleaseState.Inactive:
                return [null, null];
            default:
                return [null, null];
        }
    }

    return ['Live', runwayColors.statusGreen];
};
